import React from 'react';
import { makeStyles } from 'tss-react/mui';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, Typography, useTheme } from '@mui/material';
import Assets from '../../Component/Common/Assets';
import { isPathActive } from '../nav';

export const drawerWidth = 248;
export const miniDrawerWidth = 76;

const SHORT_NAME = {
    'Vendor Management': 'Vendors',
    'Purchase Order': 'PO',
    'Purchase Entry': 'Entry',
    'User & Role': 'Users',
};

const useStyles = makeStyles()((theme) => ({
    customList: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.info.main,
        flexGrow: 1,
        gap: '2px',
    },
    listItem: {
        borderRadius: '8px',
        zIndex: 2,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        "& .MuiTypography-root": {
            fontFamily: "Satoshi-Medium, sans-serif",
            fontSize: "13px",
            fontWeight: 500,
        },
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
        },
    },
    activeItem: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        boxShadow: '0 4px 12px -6px rgba(231, 120, 67, 0.55)',
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
        },
        '& .MuiListItemIcon-root': {
            color: theme.palette.common.white,
        },
        "& .MuiTypography-root": {
            fontFamily: "Satoshi-Bold, sans-serif",
            fontWeight: 700,
        },
    },
    normalItem: {
        backgroundColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.72)',
        '& .MuiListItemIcon-root': {
            color: 'rgba(255, 255, 255, 0.72)',
        },
    },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1.25),
    backgroundColor: theme.palette.info.main,
    minHeight: 52,
}));

const Sidebar = ({ open, handleDrawerToggle, logOut, filteredTabs }) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const location = useLocation();
    const navigate = useNavigate();

    const handleMenuClick = (item) => {
        if (item.path) navigate(item.path);
    };

    return (
        <Drawer
            sx={{
                width: open ? drawerWidth : miniDrawerWidth,
                flexShrink: 0,
                whiteSpace: open ? 'nowrap' : 'normal',
                '& .MuiDrawer-paper': {
                    width: open ? drawerWidth : miniDrawerWidth,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    overflowX: 'hidden',
                    backgroundColor: theme.palette.info.main,
                    borderRight: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    whiteSpace: open ? 'nowrap' : 'normal',
                },
            }}
            variant="permanent"
            open={open}
        >
            <DrawerHeader sx={{ justifyContent: open ? "space-between" : "center", gap: 1 }}>
                {open && (
                    <Box display={"flex"} gap={1} alignItems={"center"} minWidth={0}>
                        <Assets absolutePath={true} height={"32px"} width={"32px"} src={`/assets/img/Logo.svg`} alt="Spoton Coatings" />
                        <Typography
                            fontSize={"15px"}
                            fontWeight={700}
                            fontFamily={"Satoshi-Bold"}
                            color={theme.palette.bgWhite.main}
                            sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}
                            noWrap
                        >
                            Spoton Coating
                        </Typography>
                    </Box>
                )}
                <Box
                    onClick={handleDrawerToggle}
                    role="button"
                    aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDrawerToggle(); }}
                    sx={{
                        cursor: "pointer",
                        height: "28px",
                        width: "28px",
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderRadius: "8px",
                        border: `1px solid rgba(255,255,255,0.1)`,
                        transition: 'background-color 0.15s ease',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                >
                    {open
                        ? <ChevronLeftIcon sx={{ color: theme.palette.bgWhite.main, fontSize: 20 }} />
                        : <ChevronRightIcon sx={{ color: theme.palette.bgWhite.main, fontSize: 20 }} />}
                </Box>
            </DrawerHeader>

            <Box px={open ? 1.5 : 1} pb={0.5}>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 0.5 }}>
                <List className={classes.customList} sx={{ padding: open ? '2px 8px 8px' : '2px 4px 8px' }}>
                    {filteredTabs?.map((item, idx) => {
                        const active = isPathActive(location.pathname, item.path);
                        const prevSection = idx > 0 ? filteredTabs[idx - 1]?.section : null;
                        const showSection = open && item.section && item.section !== prevSection;
                        const label = open ? item.name : (SHORT_NAME[item.name] || item.name);
                        return (
                            <React.Fragment key={item.name}>
                                {showSection && (
                                    <Typography
                                        sx={{
                                            px: 1.25,
                                            pt: idx === 0 ? 0.25 : 1,
                                            pb: 0.4,
                                            fontSize: '10px',
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: 'rgba(255,255,255,0.38)',
                                            fontFamily: 'Satoshi-Bold, sans-serif',
                                        }}
                                    >
                                        {item.section}
                                    </Typography>
                                )}
                                <ListItem
                                    disablePadding
                                    className={`${classes.listItem} ${active ? classes.activeItem : classes.normalItem}`}
                                    sx={{
                                        "&:hover .list-item-icon": { display: "none" },
                                        "&:hover .list-item-icon-active": { display: "flex" },
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleMenuClick(item)}
                                        sx={{
                                            flexDirection: open ? 'row' : 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            px: open ? 1.25 : 0.4,
                                            py: open ? 0.5 : 0.6,
                                            gap: open ? 0 : 0.25,
                                            borderRadius: open ? '8px' : '10px',
                                            minHeight: open ? 36 : 48,
                                        }}
                                    >
                                        <ListItemIcon
                                            className="list-item-icon"
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 1.25 : 0,
                                                justifyContent: "center",
                                                display: active ? "none" : "flex",
                                                '& img': { opacity: 0.85, width: 18, height: 18 },
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemIcon
                                            className="list-item-icon-active"
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 1.25 : 0,
                                                justifyContent: "center",
                                                display: active ? "flex" : "none",
                                                '& img': { width: 18, height: 18 },
                                            }}
                                        >
                                            {item.activeIcon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={label}
                                            sx={{
                                                m: 0,
                                                width: open ? 'auto' : '100%',
                                                '& .MuiTypography-root': {
                                                    whiteSpace: open ? 'nowrap' : 'normal',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    textAlign: open ? 'left' : 'center',
                                                    fontSize: open ? 13 : 9,
                                                    lineHeight: 1.15,
                                                    fontFamily: active ? 'Satoshi-Bold, sans-serif' : 'Satoshi-Medium, sans-serif',
                                                },
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </List>
            </Box>

            <Box
                onClick={logOut}
                role="button"
                aria-label="Logout"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') logOut(); }}
                sx={{
                    cursor: "pointer",
                    mx: open ? 1 : 0.75,
                    mb: 1,
                    mt: 'auto',
                    display: 'flex',
                    flexDirection: open ? 'row' : 'column',
                    justifyContent: open ? 'space-between' : 'center',
                    alignItems: 'center',
                    gap: open ? 0.75 : 0.25,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '10px',
                    padding: open ? '8px 10px' : '6px 4px',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(231, 120, 67, 0.12)',
                        borderColor: 'rgba(231, 120, 67, 0.35)',
                    },
                }}
            >
                <Box
                    sx={{
                        height: "28px",
                        width: "28px",
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: "rgba(16, 25, 40, 0.8)",
                        borderRadius: "10px",
                        border: `1px solid rgba(255,255,255,0.1)`,
                        order: open ? 2 : 1,
                    }}
                >
                    <Assets absolutePath={true} src={`/assets/img/Logout.svg`} alt="" />
                </Box>
                <Typography sx={{
                    color: theme.palette.bgWhite.main,
                    fontSize: open ? "13px" : 9,
                    fontFamily: 'Satoshi-Medium, sans-serif',
                    lineHeight: 1.15,
                    textAlign: 'center',
                    order: open ? 1 : 2,
                }}>
                    Logout
                </Typography>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
